import React from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import {
  TrendingUp, Building2, Download, Briefcase, FileText, ArrowUpRight, ChevronRight,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Investor Portal — institutional capital-partner view.
// Tone: premium · minimal · institutional. Bronze accent on espresso panels.
// Route: /investor-portal
// ─────────────────────────────────────────────────────────────────────────────
export default function InvestorPortal() {
  const { user } = useAuth();

  return (
    <Layout>
      <div className="max-w-[1240px] mx-auto px-6 py-8" data-testid="investor-portal-page">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase"
              style={{ background: '#1A1410', color: '#A47148', border: '1px solid #A4714855' }}>
              <Briefcase className="h-3 w-3" /> Investor Portal · LP
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-[#0E1320]">
              Capital position, {user?.full_name || 'Partner'}.
            </h1>
            <p className="mt-1.5 text-[14px] text-[#4A5263] max-w-2xl">
              Quarterly returns, distribution history, and institutional reporting — designed for capital partners and family offices.
            </p>
          </div>
          <button className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#1A1410] text-white text-[12.5px] font-medium hover:bg-[#2A1F18] transition-colors" data-testid="investor-download-pack">
            <Download className="h-4 w-4" /> Download Q3 investor pack
          </button>
        </div>

        {/* Key institutional KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { k: 'committed',  label: 'Committed · SAR',   val: '24,500,000', icon: Briefcase },
            { k: 'deployed',   label: 'Deployed',          val: '21,180,000', icon: TrendingUp },
            { k: 'irr',        label: 'Net IRR',           val: '14.2%',      icon: TrendingUp },
            { k: 'distributed',label: 'Distributed YTD',   val: '3,420,000',  icon: Download },
          ].map(({ k, label, val, icon: Icon }) => (
            <div key={k} className="rounded-xl border border-[#E6EAF0] bg-white p-4" data-testid={`investor-kpi-${k}`}>
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4" style={{ color: '#A47148' }} strokeWidth={1.7} />
                <span className="text-[10px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{label}</span>
              </div>
              <div className="mt-3 text-2xl font-semibold text-[#0E1320]">{val}</div>
            </div>
          ))}
        </div>

        {/* Assets table */}
        <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-[#F1F4F7] flex items-center justify-between">
            <h3 className="text-[14px] font-semibold text-[#0E1320]">Holdings</h3>
            <span className="text-[11px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">Asset · Strategy · NOI</span>
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-[#FBFCFD] text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
              <tr>
                <th className="text-start px-4 py-3 font-medium">Asset</th>
                <th className="text-start px-4 py-3 font-medium">Strategy</th>
                <th className="text-start px-4 py-3 font-medium">Acquisition</th>
                <th className="text-end px-4 py-3 font-medium">NOI · SAR/yr</th>
                <th className="text-end px-4 py-3 font-medium">Cap rate</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Olaya Mixed-Use Tower',  strategy: 'Core+',         year: 2022, noi: '4,820,000', cap: '6.8%' },
                { name: 'Dhahran Logistics Hub',  strategy: 'Value-add',     year: 2023, noi: '2,140,000', cap: '8.1%' },
                { name: 'Diriyah Hospitality I',  strategy: 'Development',   year: 2024, noi: '—',         cap: '—'   },
              ].map((row) => (
                <tr key={row.name} className="border-t border-[#F1F4F7]" data-testid={`investor-asset-${row.name.toLowerCase().replace(/\s+/g, '-').slice(0, 24)}`}>
                  <td className="px-4 py-3 text-[#0E1320] font-medium">{row.name}</td>
                  <td className="px-4 py-3 text-[#4A5263]">{row.strategy}</td>
                  <td className="px-4 py-3 text-[#4A5263] reva-mono">{row.year}</td>
                  <td className="px-4 py-3 text-end text-[#0E1320] reva-mono">{row.noi}</td>
                  <td className="px-4 py-3 text-end text-[#0E1320] reva-mono">{row.cap}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Reports library */}
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { title: 'Quarterly investor report · Q3 2025', date: '15 Oct 2025' },
            { title: 'Annual audited statements · 2024',    date: '28 Mar 2025' },
            { title: 'AI capital forecast · 2026 outlook',   date: '02 Jan 2026' },
          ].map((r) => (
            <a key={r.title} href="#" className="group rounded-xl border border-[#E6EAF0] bg-white p-4 hover:border-[#1A1410] transition-colors flex items-start gap-3"
              data-testid={`investor-report-${r.title.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}>
              <div className="h-8 w-8 rounded-full bg-[#FBFAF7] border border-[#A4714822] flex items-center justify-center shrink-0">
                <FileText className="h-4 w-4" style={{ color: '#A47148' }} strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#0E1320]">{r.title}</div>
                <p className="mt-0.5 text-[11.5px] text-[#71777E] reva-mono">{r.date}</p>
              </div>
              <ArrowUpRight className="h-4 w-4 text-[#71777E] group-hover:text-[#0E1320]" />
            </a>
          ))}
        </div>
      </div>
    </Layout>
  );
}
